'use client';

import { useMemo, useEffect, useCallback } from 'react';

import {
  FieldFunctionOptions,
  makeVar,
  OperationVariables,
  Reference,
  TypedDocumentNode,
  ApolloClient,
} from '@apollo/client';
import {
  useApolloClient,
  useQuery,
  useReactiveVar,
  useSubscription,
} from '@apollo/client/react';
import { DocumentNode } from 'graphql';

import { getCache, readReferenceWhere } from '../cache';
import { generateQueryFromFragment, getFragmentTypename } from '../utils';

export type CreatePaginationHookOptions = {
  paginationFieldName: string;
  fragment: DocumentNode;
  filter: (
    ref: Reference,
    readField: FieldFunctionOptions['readField'],
    options: FieldFunctionOptions
  ) => boolean;
  sort: (
    refA: Reference,
    refB: Reference,
    readField: FieldFunctionOptions['readField']
  ) => number;
  isRefresh: boolean;
};

type PaginationContext<TItem = unknown> = {
  isInitialized: boolean;
  loading: boolean;
  start: { loading?: boolean; cursor?: TItem; lastCall?: Date };
  end: { loading?: boolean; cursor?: TItem; lastCall?: Date };
};

/**
 * Factory pour crééer les hooks pour une pagination par curseur.
 */
export const createPaginationHook = <TItem = unknown>({
  paginationFieldName,
  fragment,
  filter,
  sort,
  isRefresh,
}: CreatePaginationHookOptions) => {
  // Ajoute un nouveau champ dans le cache permettant de récupérer
  // une liste ordonnée et filtrée de référence.
  const cache = getCache();
  const typename = getFragmentTypename(fragment);

  // readReferenceWhere parcourt le cache sans qu'Apollo enregistre de dépendance :
  // ce compteur, incrémenté à chaque nouvelle entité du type, force la relecture
  // de la liste (ex. nouveau commentaire, y compris en optimistic).
  const itemsVersionVar = makeVar(0);

  cache.policies.addTypePolicies({
    [typename]: {
      fields: {
        id: {
          merge: (existing, incoming) => {
            if (existing === undefined) {
              itemsVersionVar(itemsVersionVar() + 1);
            }
            return incoming;
          },
        },
      },
    },
    Query: {
      fields: {
        [paginationFieldName]: {
          read: (_, options) => {
            itemsVersionVar();

            if (!options.variables?.isInitialized) {
              return undefined;
            }

            const items = readReferenceWhere(cache, {
              __typename: typename,
              readField: options.readField,
              filter: (ref, readField) => {
                return filter(ref, readField, options);
              },
            });

            if (!items) {
              return undefined;
            }

            const sortedItems = [...items].sort((refA, refB) => {
              return sort(refA, refB, options.readField);
            });
            const startIndex = options.variables.startCursor
              ? sortedItems.findIndex(
                  ({ __ref }) =>
                    __ref ===
                    `${options.variables?.startCursor.__typename}:${options.variables?.startCursor.id}`
                )
              : 0;
            const endIndex = options.variables.endCursor
              ? sortedItems.findIndex(
                  ({ __ref }) =>
                    __ref ===
                    `${options.variables?.endCursor.__typename}:${options.variables?.endCursor.id}`
                ) + 1
              : sortedItems.length;

            return sortedItems.slice(startIndex, endIndex);
          },
        },
      },
    },
  });

  // Créé la query pour appliquer le fragment à la liste de référence
  const query = generateQueryFromFragment({
    fieldName: paginationFieldName,
    fragment,
  });

  const paginationContextVar = makeVar<PaginationContext<TItem>>({
    loading: true,
    isInitialized: false,
    start: {},
    end: {},
  });

  // Hook pour récupérer une liste de donnée entre deux curseurs.
  const usePaginateItems = <
    TData = unknown,
    TQuery extends ApolloClient.QueryOptions = ApolloClient.QueryOptions,
  >(options: {
    queryOptions: TQuery & { skip?: boolean };
    normalize: (data: ApolloClient.QueryResult<TData>) => {
      startCursor?: TItem;
      endCursor?: TItem;
    };
  }) => {
    const apollo = useApolloClient();
    const paginationContext = useReactiveVar(paginationContextVar);

    const result = useQuery<
      { [x: string]: TItem[] },
      {
        isInitialized: boolean;
        startCursor?: TItem;
        endCursor?: TItem;
      }
    >(query, {
      variables: {
        ...(options.queryOptions.variables || {}),
        isInitialized: paginationContext.isInitialized,
        startCursor: !isRefresh ? paginationContext.start.cursor : undefined,
        endCursor: paginationContext.end.cursor,
      },
      fetchPolicy: 'cache-only',
    });

    useEffect(() => {
      paginationContextVar({
        loading: true,
        isInitialized: false,
        start: {},
        end: {},
      });

      if (!options.queryOptions.skip) {
        apollo.query(options.queryOptions).then((data) => {
          const { startCursor, endCursor } = options.normalize(
            data as ApolloClient.QueryResult<TData>
          );
          paginationContextVar({
            ...paginationContext,
            start: { cursor: startCursor, lastCall: new Date() },
            end: { cursor: endCursor, lastCall: new Date() },
            loading: false,
            isInitialized: true,
          });
        });
      }
    }, [
      JSON.stringify(options.queryOptions.variables),
      options.queryOptions.skip,
    ]);

    return useMemo(
      () => ({
        items: result.data?.[paginationFieldName],
        loading: result.loading,
        error: result.error,
      }),
      [result.data, result.loading, result.error]
    );
  };

  const useLoadMore = <
    TData = unknown,
    TQuery extends ApolloClient.QueryOptions = ApolloClient.QueryOptions,
  >(options: {
    queryOptions: TQuery;
    normalize: (data: ApolloClient.QueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const apollo = useApolloClient();
    const paginationContext = useReactiveVar(paginationContextVar);

    const execute = useCallback(
      (variables?: TQuery['variables'] & { limit?: number }) => {
        const limit = variables?.limit || 10;

        paginationContextVar({
          ...paginationContext,
          end: {
            ...(paginationContext.end || {}),
            loading: true,
          },
        });

        // TODO: readfromcache

        return apollo
          .query({
            ...options.queryOptions,
            variables: options.normalizeVariables(
              {
                ...(options.queryOptions.variables || {}),
                ...(variables || {}),
                limit,
              },
              paginationContext.end
            ),
          })
          .then((data) => {
            const newItems = options.normalize(
              data as ApolloClient.QueryResult<TData>
            );
            paginationContextVar({
              ...paginationContext,
              end: {
                loading: false,
                cursor:
                  newItems && newItems.length >= limit
                    ? newItems[limit - 1]
                    : undefined,
                lastCall: new Date(),
              },
            });
          });
      },
      [options.queryOptions]
    );

    return useMemo(
      () => ({
        execute,
        status: {
          loading: paginationContext.end.loading,
          hasMore: !!paginationContext.end.cursor,
          cursor: paginationContext.end.cursor,
        },
      }),
      [execute, paginationContext.end.loading, paginationContext.end.cursor]
    );
  };

  const useLoadPrev = <
    TData = unknown,
    TQuery extends ApolloClient.QueryOptions = ApolloClient.QueryOptions,
  >(options: {
    queryOptions: TQuery;
    normalize: (data: ApolloClient.QueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const apollo = useApolloClient();
    const paginationContext = useReactiveVar(paginationContextVar);

    const execute = useCallback(
      (variables?: TQuery['variables'] & { limit?: number }) => {
        const limit = variables?.limit || 10;

        paginationContextVar({
          ...paginationContext,
          start: {
            ...(paginationContext.start || {}),
            loading: true,
          },
        });

        // TODO: readfromcache

        return apollo
          .query({
            ...options.queryOptions,
            variables: options.normalizeVariables(
              {
                ...(options.queryOptions.variables || {}),
                ...(variables || {}),
                limit,
              },
              paginationContext.start
            ),
          })
          .then((data) => {
            const newItems = options.normalize(
              data as ApolloClient.QueryResult<TData>
            );
            paginationContextVar({
              ...paginationContext,
              start: {
                loading: false,
                cursor:
                  newItems && newItems.length >= limit
                    ? newItems[limit - 1]
                    : undefined,
                lastCall: new Date(),
              },
            });
          });
      },
      [options.queryOptions, paginationContext]
    );

    return useMemo(
      () => ({
        execute,
        status: {
          loading: paginationContext.start.loading,
          hasMore: !!paginationContext.start.cursor,
          cursor: paginationContext.start.cursor,
        },
      }),
      [execute, paginationContext.start.loading, paginationContext.start.cursor]
    );
  };

  const useRefresh = <
    TData = unknown,
    TQuery extends useQuery.Options = useQuery.Options,
  >(options: {
    queryOptions: TQuery & { query: ApolloClient.QueryOptions['query'] };
    normalize: (data: ApolloClient.QueryResult<TData>) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQuery['variables'],
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQuery['variables'];
  }) => {
    const paginationContext = useReactiveVar(paginationContextVar);
    const { query, ...queryOptions } = options.queryOptions;

    const refreshQuery = useQuery(query, {
      ...queryOptions,
      variables: options.normalizeVariables(
        {
          ...(queryOptions.variables || {}),
        },
        paginationContext.start
      ),
    });

    const execute = useCallback(
      async (variables?: TQuery['variables']) => {
        paginationContextVar({
          ...paginationContext,
          start: {
            ...(paginationContext.start || {}),
            loading: true,
          },
        });

        return refreshQuery
          .refetch(
            options.normalizeVariables(
              {
                ...(options.queryOptions.variables || {}),
                ...(variables || {}),
              },
              paginationContext.start
            )
          )
          .then((result) => {
            const newItems = options.normalize(
              result as ApolloClient.QueryResult<TData>
            );
            paginationContextVar({
              ...paginationContext,
              start: {
                loading: false,
                cursor: newItems?.[0] || paginationContext.start.cursor,
                lastCall: new Date(),
              },
            });
          });
      },
      [options.queryOptions, paginationContext]
    );

    return useMemo(
      () => ({
        execute,
        status: {
          loading: paginationContext.start.loading,
          cursor: paginationContext.start.cursor,
        },
      }),
      [execute, paginationContext.start.loading, paginationContext.start.cursor]
    );
  };

  const useAutoRefresh = <
    TQueryData = unknown,
    TQueryVariables extends OperationVariables = OperationVariables,
    TSyncQueryVariables extends OperationVariables = OperationVariables,
  >(options: {
    queryOptions: useQuery.Options<TQueryData, TQueryVariables> & {
      query: DocumentNode | TypedDocumentNode<TQueryData, TQueryVariables>;
    };
    syncQueryOptions: useSubscription.Options<unknown, TSyncQueryVariables> & {
      query: DocumentNode | TypedDocumentNode<unknown, TSyncQueryVariables>;
    };
    normalize: (
      data: ApolloClient.QueryResult<TQueryData>
    ) => TItem[] | undefined;
    normalizeVariables: (
      variables: TQueryVariables | undefined,
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TQueryVariables | undefined;
    normalizeSyncVariables?: (
      variables: TSyncQueryVariables | undefined,
      context: { loading?: boolean; cursor?: TItem; lastCall?: Date }
    ) => TSyncQueryVariables | undefined;
  }) => {
    const paginationContext = useReactiveVar(paginationContextVar);
    const refreshQuery = useRefresh({
      queryOptions: options.queryOptions as any,
      normalize: options.normalize,
      normalizeVariables: options.normalizeVariables as any,
    });
    const { query, variables, ...subscriptionOptions } =
      options.syncQueryOptions;

    useSubscription(query, {
      ...subscriptionOptions,
      variables: options.normalizeSyncVariables
        ? options.normalizeSyncVariables(
            variables || ({} as TSyncQueryVariables),
            paginationContext.start
          )
        : variables,
      // La subscription pour les nouveaux posts est active seulement après le début du live si on laisse le skip
      // skip: !paginationContext.start.cursor,
      onData: () => {
        if (!refreshQuery.status.loading) {
          refreshQuery.execute();
        }
      },
    } as useSubscription.Options<unknown, TSyncQueryVariables>);

    return refreshQuery;
  };

  return {
    usePaginateItems,
    useLoadMore,
    useLoadPrev,
    useRefresh,
    useAutoRefresh,
  };
};
